import React, { ReactNode } from "react";
import { activeNavHref } from "@/lib/active-nav";
import { Link, useLocation } from "wouter";
import {
  Settings,
  Menu,
  X,
  ChevronRight,
  Home,
  User,
  Briefcase,
  GraduationCap,
  ArrowRightLeft,
  LogOut,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useFirebaseAuth } from "@/hooks/use-firebase-auth";
import { usePanelAccess } from "@/hooks/use-panel-access";
import {
  canAccessSellerPanel,
  canAccessTeachingPanel,
  TEACHING_ENTRY_PATH,
} from "@/lib/panel-switcher-access";
import Navbar from "./navbar";

interface ProfessionalPanelLayoutProps {
  children: ReactNode;
}

const professionalNavItems = [
  { title: "Dashboard", href: "/professional/dashboard", icon: Home },
  { title: "Profile", href: "/professional/profile", icon: User },
  { title: "Jobs", href: "/professional/jobs", icon: Briefcase },
  { title: "Settings", href: "/professional/settings", icon: Settings },
];

export default function ProfessionalPanelLayout({ children }: ProfessionalPanelLayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia("(min-width: 1024px)").matches;
  });
  const [location, navigate] = useLocation();
  const { toast } = useToast();
  const { logout: handleLogout, user } = useFirebaseAuth();
  const { isNobility, isRoyalty, isImperial, subscriptionTier, subscriptionStatus } = usePanelAccess();

  const sub = { isNobility, isRoyalty, isImperial, subscriptionTier, subscriptionStatus };
  const hasSellerAccess = canAccessSellerPanel(user, sub);
  const showTeaching = canAccessTeachingPanel(user, sub);
  const hasOtherPanels = hasSellerAccess || showTeaching;

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  // Sub-pages count, as in the other panels.
  const activeHref = activeNavHref(
    location,
    professionalNavItems.map((i) => (i.href === "/professional/dashboard" ? { ...i, exactFor: ["/professional"] } : i))
  );
  const isActive = (href: string) => activeHref === href;

  const switcherButtonClass = cn(
    "w-full h-10 border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white",
    "justify-start px-3"
  );

  const handleLogoutClick = async () => {
    try {
      await handleLogout();
      toast({
        title: "Logged out",
        description: "You have been successfully logged out.",
      });
    } catch {
      toast({
        title: "Logout failed",
        description: "There was an error logging you out. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-900 text-gray-100">
      <Navbar />
      {/* Navbar is `position: fixed` and no longer occupies flow height. This
          shell is a fixed h-screen box, so `margin-top` (not padding) is the
          correct compensation — see instructor-panel-layout.tsx for why. */}
      <div className="flex flex-1 overflow-hidden mt-[var(--app-navbar-height,160px)]">
        <aside
          className={cn(
            "hidden lg:flex flex-col shrink-0 bg-gray-800 border-r border-gray-700 transition-all duration-300 ease-in-out z-50 h-full overflow-hidden",
            isSidebarOpen ? "w-52" : "w-16"
          )}
        >
          <div className="flex h-16 items-center justify-between border-b border-gray-700 px-4">
            {isSidebarOpen ? (
              <Link href="/professional/dashboard">
                <button
                  type="button"
                  className="flex items-center gap-2 font-semibold hover:bg-gray-700/50 p-2 rounded-lg transition-colors"
                >
                  <Briefcase className="h-6 w-6 text-emerald-400" />
                  <span>Professional</span>
                </button>
              </Link>
            ) : (
              <div className="w-6" />
            )}
            <Button variant="ghost" size="icon" onClick={toggleSidebar}>
              {isSidebarOpen ? <ChevronRight className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </Button>
          </div>

          <nav className="flex-1 grid gap-1 px-2 py-2 content-start overflow-y-auto">
            {professionalNavItems.map((item) => (
              <Link key={item.href} href={item.href}>
                <Button
                  variant={isActive(item.href) ? "default" : "ghost"}
                  size="sm"
                  className={cn(
                    "w-full h-10",
                    isSidebarOpen ? "justify-start px-3" : "justify-center px-0",
                    isActive(item.href) && "bg-emerald-600 text-white hover:bg-emerald-600 font-semibold"
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  {isSidebarOpen && <span className="ml-2 truncate">{item.title}</span>}
                </Button>
              </Link>
            ))}
          </nav>

          {hasOtherPanels && (
            <div className="border-t border-gray-700 p-2 grid gap-1">
              {hasSellerAccess && (
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(switcherButtonClass, !isSidebarOpen && "justify-center px-0")}
                  onClick={() => navigate("/seller")}
                >
                  <ArrowRightLeft className="h-4 w-4 shrink-0" />
                  {isSidebarOpen && (
                    <span className="ml-2 truncate text-xs">Switch to Seller</span>
                  )}
                </Button>
              )}
              {showTeaching && (
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(switcherButtonClass, !isSidebarOpen && "justify-center px-0")}
                  onClick={() => navigate(TEACHING_ENTRY_PATH)}
                >
                  <GraduationCap className="h-4 w-4 shrink-0" />
                  {isSidebarOpen && (
                    <span className="ml-2 truncate text-xs">Teaching &amp; courses</span>
                  )}
                </Button>
              )}
            </div>
          )}

          <div className="border-t border-gray-700 p-2">
            <Button
              variant="ghost"
              className={cn(
                "w-full h-10 text-gray-400 hover:text-white",
                isSidebarOpen ? "justify-start px-3" : "justify-center px-0"
              )}
              onClick={handleLogoutClick}
            >
              <LogOut className="h-4 w-4" />
              {isSidebarOpen && <span className="ml-2 text-xs">Sign out</span>}
            </Button>
            {isSidebarOpen && (
              <p className="text-xs text-gray-400 text-center mt-2">v{__APP_VERSION__}</p>
            )}
          </div>
        </aside>

        {isSidebarOpen && (
          <>
            <aside className="fixed inset-y-0 left-0 z-[60] flex flex-col bg-gray-800 w-52 lg:hidden border-r border-gray-700">
              <div className="flex h-16 items-center justify-between border-b border-gray-700 px-4">
                <Link href="/professional/dashboard">
                  <button
                    type="button"
                    className="flex items-center gap-2 font-semibold hover:bg-gray-700/50 p-2 rounded-lg transition-colors"
                  >
                    <Briefcase className="h-6 w-6 text-emerald-400" />
                    <span>Professional</span>
                  </button>
                </Link>
                <Button variant="ghost" size="icon" onClick={toggleSidebar}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <nav className="flex-1 grid gap-1 px-2 py-2 content-start overflow-y-auto">
                {professionalNavItems.map((item) => (
                  <Link key={`m-${item.href}`} href={item.href}>
                    <Button
                      variant={isActive(item.href) ? "default" : "ghost"}
                      className={cn(
                        "w-full justify-start",
                        isActive(item.href) && "bg-emerald-600 text-white hover:bg-emerald-600"
                      )}
                      onClick={() => setIsSidebarOpen(false)}
                    >
                      <item.icon className="h-5 w-5" />
                      <span className="ml-2">{item.title}</span>
                    </Button>
                  </Link>
                ))}
              </nav>
              {hasOtherPanels && (
                <div className="border-t border-gray-700 p-2 grid gap-1">
                  {hasSellerAccess && (
                    <Button
                      variant="outline"
                      size="sm"
                      className={switcherButtonClass}
                      onClick={() => {
                        setIsSidebarOpen(false);
                        navigate("/seller");
                      }}
                    >
                      <ArrowRightLeft className="h-4 w-4" />
                      <span className="ml-2 truncate text-xs">Switch to Seller</span>
                    </Button>
                  )}
                  {showTeaching && (
                    <Button
                      variant="outline"
                      size="sm"
                      className={switcherButtonClass}
                      onClick={() => {
                        setIsSidebarOpen(false);
                        navigate(TEACHING_ENTRY_PATH);
                      }}
                    >
                      <GraduationCap className="h-4 w-4" />
                      <span className="ml-2 truncate text-xs">Teaching &amp; courses</span>
                    </Button>
                  )}
                </div>
              )}
              <div className="border-t border-gray-700 p-2">
                <Button variant="ghost" className="w-full justify-start" onClick={handleLogoutClick}>
                  <LogOut className="h-4 w-4" />
                  <span className="ml-2 text-xs">Sign out</span>
                </Button>
                <p className="text-xs text-gray-400 text-center mt-2">v{__APP_VERSION__}</p>
              </div>
            </aside>
            <button
              type="button"
              className="fixed inset-0 z-40 bg-black/80 lg:hidden"
              onClick={toggleSidebar}
              aria-label="Close professional panel navigation"
            />
          </>
        )}

        <div className="flex flex-1 flex-col overflow-hidden">
          <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-gray-700 bg-gray-800 px-3 sm:px-4 lg:hidden">
            <Button variant="ghost" size="icon" className="h-11 w-11" onClick={toggleSidebar}>
              <Menu className="h-6 w-6" />
            </Button>
            <div className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-emerald-400" />
              <span className="font-semibold">Professional Panel</span>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto bg-gray-900 p-3 text-white sm:p-4 lg:p-6">
            <div className="max-w-7xl mx-auto">
              {/* Banner removed: these screens were checked at 390px and lay
                  out correctly -- cards stack, buttons go full width, nothing
                  overflows. Telling someone to go and find a desktop, above
                  the content, on a phone, is the product apologising for
                  itself. If a screen here stops working small, fix that
                  screen. */}
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
